import { ExecutivePower, PolicyFaction } from './enums';

export const MIN_PLAYERS = 5;
export const MAX_PLAYERS = 10;

/** Spec §10. */
export interface FactionDistribution {
  separatists: number;
  ordinaryFederalists: number;
  nenshi: 1;
}

export const PLAYER_DISTRIBUTION: Readonly<Record<number, FactionDistribution>> = {
  5: { separatists: 3, ordinaryFederalists: 1, nenshi: 1 },
  6: { separatists: 4, ordinaryFederalists: 1, nenshi: 1 },
  7: { separatists: 4, ordinaryFederalists: 2, nenshi: 1 },
  8: { separatists: 5, ordinaryFederalists: 2, nenshi: 1 },
  9: { separatists: 5, ordinaryFederalists: 3, nenshi: 1 },
  10: { separatists: 6, ordinaryFederalists: 3, nenshi: 1 },
};

/** Spec §41. */
export interface TrackSpace {
  position: number;
  power: ExecutivePower;
}

export type PolicyDeckComposition = Readonly<Record<PolicyFaction, number>>;

/**
 * Everything the engine needs to run one game. Resolved once for the player
 * count at game start and stored with the game, so a restart or a later
 * config change never alters the rules of a game already in progress.
 */
export interface GameRules {
  playerCount: number;
  distribution: FactionDistribution;
  deck: PolicyDeckComposition;
  separationPoliciesToWin: number;
  federalistPoliciesToWin: number;
  /** Federalist policies enacted before Nenshi-as-Premier wins (spec §9, §28). */
  nenshiThreshold: number;
  gridlockLimit: number;
  /** Cards the Minister draws each legislative session. */
  legislativeDraw: number;
  /** Cards shown by Internal Polling. */
  pollingPeek: number;
  federalistTrack: TrackSpace[];
  /** Spec §22: in 5–6 player games Nenshi knows the ordinary Federalists. */
  nenshiKnowsFederalists: boolean;
  /**
   * When this many or fewer players remain active, only the last elected
   * Premier is barred from the next Premier nomination; above it the last
   * elected Minister is barred too.
   */
  smallTableActivePlayers: number;
}

export const POLICY_DECK_COMPOSITION: PolicyDeckComposition = {
  [PolicyFaction.FEDERALIST]: 11,
  [PolicyFaction.SEPARATION]: 6,
};

const { NONE, CAMPAIGN_RESEARCH, INTERNAL_POLLING, UNITY_GOVERNMENT, COURT_CHALLENGE, COURT_CHALLENGE_AND_VETO, FEDERALIST_VICTORY } =
  ExecutivePower;

function track(...powers: ExecutivePower[]): TrackSpace[] {
  return powers.map((power, i) => ({ position: i + 1, power }));
}

export const FEDERALIST_POWER_SCHEDULES: ReadonlyArray<{ minPlayers: number; maxPlayers: number; track: TrackSpace[] }> = [
  {
    minPlayers: 5,
    maxPlayers: 6,
    track: track(NONE, NONE, INTERNAL_POLLING, COURT_CHALLENGE, COURT_CHALLENGE_AND_VETO, FEDERALIST_VICTORY),
  },
  {
    minPlayers: 7,
    maxPlayers: 8,
    track: track(NONE, CAMPAIGN_RESEARCH, UNITY_GOVERNMENT, COURT_CHALLENGE, COURT_CHALLENGE_AND_VETO, FEDERALIST_VICTORY),
  },
  {
    minPlayers: 9,
    maxPlayers: 10,
    track: track(CAMPAIGN_RESEARCH, CAMPAIGN_RESEARCH, UNITY_GOVERNMENT, COURT_CHALLENGE, COURT_CHALLENGE_AND_VETO, FEDERALIST_VICTORY),
  },
];

export function isValidPlayerCount(n: number): boolean {
  return Number.isInteger(n) && n >= MIN_PLAYERS && n <= MAX_PLAYERS;
}

export function getGameRules(playerCount: number): GameRules {
  if (!isValidPlayerCount(playerCount)) {
    throw new RangeError(`Player count must be ${MIN_PLAYERS}–${MAX_PLAYERS}, got ${playerCount}`);
  }
  const schedule = FEDERALIST_POWER_SCHEDULES.find((s) => playerCount >= s.minPlayers && playerCount <= s.maxPlayers);
  if (!schedule) throw new Error(`No Federalist power schedule for ${playerCount} players`);

  return {
    playerCount,
    distribution: { ...PLAYER_DISTRIBUTION[playerCount]! },
    deck: { ...POLICY_DECK_COMPOSITION },
    separationPoliciesToWin: 5,
    federalistPoliciesToWin: 6,
    nenshiThreshold: 3,
    gridlockLimit: 3,
    legislativeDraw: 3,
    pollingPeek: 3,
    federalistTrack: schedule.track.map((s) => ({ ...s })),
    nenshiKnowsFederalists: playerCount <= 6,
    smallTableActivePlayers: 5,
  };
}

/** Power granted by enacting the Nth Federalist policy (1-based). */
export function powerAtFederalistPosition(rules: GameRules, position: number): ExecutivePower {
  return rules.federalistTrack.find((s) => s.position === position)?.power ?? ExecutivePower.NONE;
}

/** Veto is available once a space carrying it has been enacted (spec §40). */
export function isVetoUnlocked(rules: GameRules, federalistPolicyCount: number): boolean {
  return rules.federalistTrack.some(
    (s) => s.position <= federalistPolicyCount && s.power === ExecutivePower.COURT_CHALLENGE_AND_VETO,
  );
}
